import { Button } from "@/components/ui/button";
import { DEFAULT_PAGE, PAGINATION_LABELS } from "@/constants/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { VOUCHER_PER_PAGE_OPTIONS } from "../constants";
import { VouchersPageMeta } from "../hooks/use-vouchers-source";
import { PAYROLL_VOUCHER_PAGINATION_LABELS } from "../messages";

interface DataTablePaginationProps {
  meta: VouchersPageMeta;
  page: number;
  perPage: number;
  isFetching: boolean;
  onChange: (page: number, perPage: number) => void;
}

export function DataTablePagination({
  meta,
  page,
  perPage,
  isFetching,
  onChange,
}: DataTablePaginationProps): JSX.Element {
  const lastPage: number = Math.max(meta.lastPage, DEFAULT_PAGE);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {PAYROLL_VOUCHER_PAGINATION_LABELS.RESULT_COUNT(meta.total)}
      </div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">
            {PAGINATION_LABELS.ROWS_PER_PAGE}
          </p>
          <Select
            value={`${perPage}`}
            onValueChange={(value: string): void =>
              onChange(DEFAULT_PAGE, Number(value))
            }
          >
            <SelectTrigger
              className="h-8 w-[70px]"
              aria-label={PAGINATION_LABELS.ROWS_PER_PAGE}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top">
              {VOUCHER_PER_PAGE_OPTIONS.map((pageSize: number) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm font-medium">
          {PAYROLL_VOUCHER_PAGINATION_LABELS.PAGE_OF(page, lastPage)}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            aria-label={PAGINATION_LABELS.PREVIOUS}
            onClick={(): void => onChange(page - 1, perPage)}
            disabled={isFetching || page <= DEFAULT_PAGE}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            aria-label={PAGINATION_LABELS.NEXT}
            onClick={(): void => onChange(page + 1, perPage)}
            disabled={isFetching || page >= lastPage}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
